import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Terminal, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const COUNTRY_CODES = [
  ["+62", "ID +62"], ["+60", "MY +60"], ["+65", "SG +65"], ["+1", "US +1"], ["+44", "UK +44"],
  ["+61", "AU +61"], ["+81", "JP +81"], ["+91", "IN +91"], ["+971", "AE +971"], ["+966", "SA +966"],
];

export default function AuthPage() {
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cc, setCc] = useState("+62");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const body = tab === "register" ? { name, email, password, country_code: cc, phone } : { email, password };
      const { data } = await axios.post(`${API}/auth/${tab === "register" ? "register" : "login"}`, body);
      localStorage.setItem("atr_token", data.token);
      if (tab === "register") {
        axios.post(`${API}/auth/send-verification`, { email }).catch(() => {});
      }
      navigate("/platform");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Gagal — periksa kembali data Anda.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="auth-page">
      <div className="terminal-grid pointer-events-none absolute inset-0 opacity-40" />
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md border border-white/15 bg-ink-surface p-8"
      >
        <a href="/" data-testid="auth-logo" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center border border-buy/50 bg-buy/10 text-buy">
            <Terminal size={16} />
          </span>
          <div><div className="font-display text-base font-bold text-white">Welcome to <span className="text-buy">NakedChart.id</span></div><div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-mute-dim">AI Trading Intelligence Platform</div></div>
        </a>

        <div className="mt-8 grid grid-cols-2 gap-px border border-white/10 bg-white/10">
          {[["login", "Masuk"], ["register", "Daftar"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setTab(key); setError(""); }}
              data-testid={`auth-tab-${key}`}
              className={`py-2.5 font-mono text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${
                tab === key ? "bg-buy/15 text-buy" : "bg-ink text-mute hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="auth-form">
          {tab === "register" && (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="nama lengkap"
              data-testid="auth-name-input"
              className="h-11 w-full border border-white/15 bg-ink px-4 font-mono text-sm outline-none transition-colors duration-200 focus:border-buy/60"
            />
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="email"
            data-testid="auth-email-input"
            className="h-11 w-full border border-white/15 bg-ink px-4 font-mono text-sm outline-none transition-colors duration-200 focus:border-buy/60"
          />
          {tab === "register" && (
            <div className="flex gap-2">
              <select value={cc} onChange={(e) => setCc(e.target.value)} data-testid="auth-cc-select"
                className="h-11 w-24 shrink-0 border border-white/15 bg-ink px-2 font-mono text-sm text-white outline-none focus:border-buy/60">
                {COUNTRY_CODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                inputMode="numeric"
                placeholder="nomor HP (mis. 812xxx)"
                data-testid="auth-phone-input"
                className="h-11 min-w-0 flex-1 border border-white/15 bg-ink px-4 font-mono text-sm outline-none transition-colors duration-200 focus:border-buy/60"
              />
            </div>
          )}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            placeholder="password (min. 6 karakter)"
            data-testid="auth-password-input"
            className="h-11 w-full border border-white/15 bg-ink px-4 font-mono text-sm outline-none transition-colors duration-200 focus:border-buy/60"
          />
          {error && <p className="font-mono text-xs text-sell" data-testid="auth-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-button"
            className="flex h-11 w-full items-center justify-center gap-2 border border-buy bg-buy/10 font-mono text-sm font-bold uppercase tracking-wider text-buy transition-colors duration-200 hover:bg-buy hover:text-ink disabled:opacity-60"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {tab === "register" ? "Buat Akun + Paper $10.000" : "Masuk ke Platform"}
          </button>
        </form>

        {tab === "login" && (<div className="mt-3 text-right"><button type="button" className="font-mono text-[10px] text-mute hover:text-buy transition-colors">Lupa Password?</button></div>)}

        <p className="mt-6 font-mono text-[10px] leading-relaxed text-mute-dim">
        </p>
      </motion.div>
    </div>
  );
}
